import React, { useState } from 'react';
import { View, Image, Keyboard } from 'react-native';
import OTPVerification from '@sharedComponents/OTPInput/OTPInput';
import axios from 'axios';
import { endpoints } from '@api/apis';
import { useDarkMode } from '@app/utils';
import { ThemedText, ThemedView } from '@app/theme';
import useIsMobileBrowser from '@app/hooks/useIsMobileBrowser';
import Button from '@app/sharedComponents/Button/Button';
import ReportOtp from '../../../../assets/Icons/ReportOtp';
import gs from '@app/styles/globalStyles';

const iconUrl = 'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/icon_holder.png';

interface VerifyEmailOtpProps {
    email: string;
    applicationId: string;
    otpDigitCount: number;
    onClose: () => void;
    onVerifySuccess: (data: any) => void;
}

export const RESPONSE_STATUS = {
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
}

export const VOLT_ERROR_CODE = {
    IN_CORRECT_OTP: 'IN_CORRECT_OTP',
    ACCOUNT_BLOCKED_FROM_OTP: 'ACCOUNT_BLOCKED_FROM_OTP',
}

const VerifyEmailOtp: React.FC<VerifyEmailOtpProps> = ({
    email,
    applicationId,
    otpDigitCount,
    onClose,
    onVerifySuccess,
}) => {
    const [hasError, setHasError] = useState<string>('');
    const [verifying, setVerifying] = useState<boolean>(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const isDarkMode = useDarkMode();
    const [isReportingMode, setIsReportingMode] = useState(false);
    const isMobileBrowser = useIsMobileBrowser();

    const submitOtp = (otp: string) => {
        setVerifying(true);
        const url = endpoints.emailVerify;
        const payload = {
            applicationId: applicationId,
            otp,
        };
        setIsBlocked(true);
        axios
            .post(url, payload, { handleFromMF: true })
            .then(res => {
                if (res.data.status === RESPONSE_STATUS.SUCCESS) {
                    onVerifySuccess(res.data);
                } else {
                    setIsBlocked(false)
                    setHasError(res.data.message || 'Verification failed');
                }
            })
            .catch(err => {
                setIsBlocked(false)
                if (
                    err?.status === 400 &&
                    err?.data?.voltErrorCode === VOLT_ERROR_CODE.IN_CORRECT_OTP
                ) {
                    setHasError(err?.data?.message || 'Incorrect OTP');
                }
                if (
                    err?.status === 400 &&
                    err?.data?.voltErrorCode === VOLT_ERROR_CODE.ACCOUNT_BLOCKED_FROM_OTP
                ) {
                    setIsBlocked(true);
                    setHasError(err?.data?.message);
                }
            })
            .finally(() => {
                setVerifying(false);
            });
    };

    const handleOtpInput = (otp: string[]) => {
        if (isBlocked) {
            return;
        }

        const OTP = otp.join('');
        if (OTP.length === otpDigitCount) {
            submitOtp(OTP);
        } else if (OTP.length > 0 && hasError !== '') {
            setHasError('');
        }
    };

    const triggerResendOTP = (): void => {
        setHasError('');
        const url = endpoints.emailInit;
        const payload = {
            emailId: email,
            applicationId: applicationId
        };
        axios
            .post(url, payload)
            .then(res => {
                console.log('OTP Resent', res);
            })
            .catch(err => {
                setHasError(err?.data?.message || 'Resend failed');
                console.log('err', err);
                if (
                    err?.status === 400 &&
                    err?.data?.voltErrorCode === 'ACCOUNT_BLOCKED_FROM_OTP'
                ) {
                    setIsBlocked(true);
                    Keyboard.dismiss();
                }
            });
    };

    return isReportingMode ? (
        <ThemedView alignItems='center' padding='lg'>
            <ReportOtp width={100} height={100} />
            <ThemedText variant='h5s'>Request Received</ThemedText>
            <ThemedText textAlign='center' marginTop='sm' variant='b3r'>
                {"Thanks for the feedback, We are working on the issue, please try again shortly."}
            </ThemedText>

            <Button handlePress={() => {
                onClose();
                setTimeout(() => {
                    setIsReportingMode(false);
                }, 1000);

            }}
                overRideStyle={{ marginTop: 24 }}
                label='Got it'
            />
        </ThemedView>
    ) : (
        <ThemedView style={[gs.setPadding(16), gs.setMarginTop(8)]}>
            <ThemedView style={[gs.flexRow, gs.spaceBetween]}>
                <ThemedView style={[gs.setMarginBottom(16)]}>
                    <ThemedText color='text-primary' variant='h3s'>
                        {`Enter ${otpDigitCount}-digit OTP`}
                    </ThemedText>
                </ThemedView>
            </ThemedView>

            <ThemedView
                bg='bg-fill-emphasis-secondary'
                borderColor='border-emphasis-secondary'
                style={[
                    gs.flexRow,
                    {
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: isDarkMode ? '#10964833' : '#CFEADA',
                        padding: 12,
                    },
                    gs.setMarginBottom(16),
                ]}>
                <Image source={{ uri: iconUrl }} style={{ width: 48, height: 48 }} />
                    <ThemedView style={{ flex: 1 }} marginLeft='md'>
                    <ThemedText color='text-secondary' variant='b3r'>
                        OTP sent to
                    </ThemedText>
                        <ThemedView style={{ marginTop: 3 }}>
                            <ThemedText color='text-primary' variant='b3m' style={{ width: '100%' }}>
                            {email}
                        </ThemedText>
                    </ThemedView>
                </ThemedView>
            </ThemedView>
            <OTPVerification
                noOfDigits={otpDigitCount}
                hasError={hasError}
                handleOtpInput={handleOtpInput}
                resendTimerCount={15}
                triggerResendOTP={triggerResendOTP}
                verifying={verifying}
                blockResend={isBlocked}
                onCustomerFacingIssue={() => {
                    setIsReportingMode(true);
                }}
                isDarkMode={Boolean(isDarkMode)}
            />
            {isMobileBrowser && <ThemedView height={30} />}
        </ThemedView>
    );
};

export default VerifyEmailOtp;
